import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getDashboardAsync } from '../../Services/DashboardService';
import Header from './../../Shared/Components/Header/Header';
import CompletionBadge from '../ELearning/Courses/CompletionBadge';
import Card from '../../Shared/Components/Card/Card';
import Table from '../../Shared/Components/Table';
import { Link } from 'react-router-dom';
import { getStatus } from '../../Infrastructure/Utilities/LearnerProgressHelper';
import { Line } from 'rc-progress';
import { getStatusBadge } from '../../Infrastructure/Utilities/LearnerProgressHelper';
import { ThemeContext } from '../../Infrastructure/Context/ThemeContext';
import LineIcon from 'react-lineicons';
import './LearnerDashboard.scss';

const LearnerDashboard = () => {
    const [dashboard, setDashboard] = useState({ courses: [], learningPathways: [] });
    const context = useContext(ThemeContext);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        let response = await getDashboardAsync("Learner");
        setDashboard(response);
    }

    const courses = useMemo(() => (dashboard.courses || []).filter((entry) => entry.course), [dashboard.courses]);
    const getCourseHasAssessment = (entry) => {
        if (entry?.hasAssessment !== undefined && entry?.hasAssessment !== null) {
            return !!entry.hasAssessment;
        }

        return !!entry?.course?.hasAssessment;
    };

    const coursesWithAssessment = useMemo(() => {
        return courses.filter((entry) => getCourseHasAssessment(entry));
    }, [courses]);

    const coursesWithoutAssessment = useMemo(() => {
        return courses.filter((entry) => !getCourseHasAssessment(entry));
    }, [courses]);
    const showCourseTables = coursesWithAssessment.length > 0;
    const stats = useMemo(() => {
        let completed = 0;
        let inProgress = 0;
        let notStarted = 0;
        let totalScore = 0;
        let scoreCount = 0;
        let progressTotal = 0;
        let progressCount = 0;

        courses.forEach((entry) => {
            if (entry.completedDateTime) {
                completed += 1;
                if (entry.scorePercentage !== null && entry.scorePercentage !== undefined) {
                    totalScore += entry.scorePercentage;
                    scoreCount += 1;
                }
            } else if (entry.startedDateTime) {
                inProgress += 1;
            } else {
                notStarted += 1;
            }

            const progressValue = entry.progress !== null && entry.progress !== undefined
                ? entry.progress
                : entry.completedDateTime
                    ? 100
                    : 0;

            progressTotal += progressValue;
            progressCount += 1;
        });

        const total = courses.length;
        const overallProgress = progressCount > 0 ? Math.round(progressTotal / progressCount) : 0;
        const averageScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;

        return { total, completed, inProgress, notStarted, overallProgress, averageScore };
    }, [courses]);

    const snapshotCards = useMemo(() => ([
        {
            label: 'Total courses',
            value: stats.total,
            icon: 'layers',
            accent: '#079fd0',
            background: '#e8f5f9'
        },
        {
            label: 'Completed',
            value: stats.completed,
            icon: 'checkmark',
            accent: '#16a34a',
            background: '#e7f6ec'
        },
        {
            label: 'In progress',
            value: stats.inProgress,
            icon: 'play',
            accent: '#ea580c',
            background: '#fef0e6'
        },
        {
            label: 'Avg. score',
            value: `${stats.averageScore}%`,
            icon: 'stats-up',
            accent: '#7c3aed',
            background: '#f0ebf8'
        }
    ]), [stats]);

    const featuredCourse = useMemo(() => {
        return courses.find((entry) => entry.startedDateTime && !entry.completedDateTime) || courses[0];
    }, [courses]);

    const featuredCourseHasAssessment = featuredCourse ? getCourseHasAssessment(featuredCourse) : false;

    const headings = (showAssessmentColumns = true) => {
        return (<>
            <th>Course name</th>
            <th>Status</th>
            {showAssessmentColumns && <th>Result</th>}
            {showAssessmentColumns && <th>score</th>}
            <th></th>
        </>)
    }

    const rowData = (items, showAssessmentColumns = true) => {
        return (
            <>
                {items.map(
                    (x, i) =>
                        <tr key={i}>
                            <td>{x.course !== null && x.course.title}</td>
                            <td>
                                {getStatus(x.completedDateTime, x.startedDateTime, getCourseHasAssessment(x), x.scorePercentage)}
                            </td>
                            {showAssessmentColumns && <td>{getStatusBadge(x.lessonStatus, x.scorePercentage)}</td>}
                            {showAssessmentColumns && <td>{x.scorePercentage !== null && x.scorePercentage !== undefined ? `${Math.round(x.scorePercentage)}%` : '-'}</td>}
                            <td>
                                <Link to={`/engage/${x.course !== null && x.course.id}`} className="btn btn-sm btn-outline-primary action-button float-right mr-3">
                                    View
                                </Link>
                            </td>
                        </tr>
                )
                }
            </>
        )
    }

    return (
        <div className="learner-dashboard-page">
            <Header heading="Dashboard" subheading="Current overview of the eLearning analytics" icon="bar-chart" />
            <div
                className="container-fluid content learner-dashboard"
                style={{
                    "--learner-accent": context.primaryButtonColour || '#079fd0'
                }}
            >
                <div className="row g-4">
                    <div className="col-12">
                        <div className="overview-card">
                            <div className="overview-title">Learning Snapshot</div>
                            <div className="overview-grid">
                                {snapshotCards.map((card) => (
                                    <div className="overview-stat" key={card.label} style={{ "--stat-accent": card.accent, "--stat-bg": card.background }}>
                                        <div className="stat-icon">
                                            <LineIcon name={card.icon} />
                                        </div>
                                        <div className="stat-meta">
                                            <span className="label">{card.label}</span>
                                            <span className="value">{card.value}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="overview-progress">
                                <div className="progress-meta">
                                    <span>Overall progress</span>
                                    <strong>{stats.overallProgress}%</strong>
                                </div>
                                <Line percent={stats.overallProgress} strokeWidth="4" strokeColor="#0ea5e9" strokeLinecap="round" trailColor="#e2e8f0" />
                            </div>
                        </div>
                    </div>

                    {showCourseTables && (
                        <div className="col-12 col-lg-7">
                            <Card title="Courses">
                                <Table headings={() => headings(true)} rowData={() => rowData(coursesWithAssessment, true)} />
                            </Card>
                            {coursesWithoutAssessment.length > 0 && (
                                <Card title="Mindfullness" classes="mt-4">
                                    <Table headings={() => headings(false)} rowData={() => rowData(coursesWithoutAssessment, false)} />
                                </Card>
                            )}
                        </div>
                    )}
                    <div className={showCourseTables ? "col-12 col-lg-5" : "col-12"}>
                        <div className="focus-card">
                            <div className="focus-header">
                                <span>Continue learning</span>
                            </div>
                            {featuredCourse ?
                                <>
                                    <h4>{featuredCourse.course.title}</h4>
                                    <p>{getStatus(featuredCourse.completedDateTime, featuredCourse.startedDateTime)}</p>
                                    <div className="focus-actions">
                                        <Link to={`/engage/${featuredCourse.course.id}`} className="btn btn-primary">
                                            Resume
                                        </Link>
                                        {featuredCourseHasAssessment && (
                                            <span className="focus-score">Score {Math.round(featuredCourse.scorePercentage || 0)}%</span>
                                        )}
                                    </div>
                                </>
                                :
                                <p className="muted">No course activity yet. Once assigned, your next step will appear here.</p>
                            }
                        </div>
                        <Card title="Badges" classes="mt-4">
                            <div className="badges-grid">
                                {courses.length > 0 ?
                                    courses.map(
                                        (c, i) => <CompletionBadge key={i} completed={c.lessonStatus === 4} image={c.course !== null ? c.course.badgeImageUrl : ''} courseId={c.course !== null && c.course.id} />
                                    )
                                    :
                                    <div className="empty-state">Complete a course to earn your first badge.</div>
                                }
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LearnerDashboard;